import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { CompanyLifecycleService } from '../../services/company-lifecycle.service';
import { CompanyPermissionsConfigService } from '../../services/company-permissions-config.service';
import { CompaniesFormConfigService } from '../../services/companies-form-config.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { HasPermissionsService } from '../../../shared/services/has-permissions/has-permissions.service';
import { AlertsType } from '@rappi/ui/alerts';
import {
  AgencyRebate,
  CompanyComment,
  CompanyDocumentRecord,
  CompanyRecord,
  ContactCreateResponse,
  ClassificationCalculateResult,
} from '../../models/makers-finances-mgr.models';
import {
  CLASSIFICATION_API_FIELD,
  CLASSIFICATION_CALCULATE_STATUS,
  CLASSIFICATION_CALCULATION_ERROR_DIALOG_RESULT,
  CLASSIFICATION_CONTINUE_LABEL,
  CLASSIFICATION_SUBMIT_STEP,
  ClassificationSubmitStep,
  InvoicingClassificationValue,
  classificationCssClass as classificationCssModifier,
} from '../company-form/company-form.classification';
import { ClassificationChangeDialogComponent } from '../company-form/dialogs/classification-change-dialog/classification-change-dialog.component';
import { ClassificationCalculationErrorDialogComponent } from '../company-form/dialogs/classification-calculation-error-dialog/classification-calculation-error-dialog.component';
import {
  COMPANY_ACTION,
  COMPANY_DIALOG_CONFIG,
  COMPANY_DIALOG_TYPE,
  COMPANY_DOCUMENT_COPY,
  COMPANY_DOCUMENT_FIELD,
  COMPANY_DOCUMENT_LABELS,
  COMPANY_DOCUMENT_ORDER,
  COMPANY_PERMISSIONS,
  COMPANY_QUERY_PARAM,
  COMPANY_STATUS_LABELS,
  COMPANY_SUCCESS_MESSAGE,
  COMPANY_VERIFICATION_ISSUE_BANNER,
  COMPANY_TRATO_LABEL,
  COMPANY_TYPE,
  COMPANY_TYPE_LABELS,
  COMPANY_VIEW_TOOLTIP,
  CompanyDialogType,
  CompanyDocumentField,
  CompanyStatus,
  CompanyStatusMeta,
  CompanyType,
  normalizeCompanyStatus,
} from '../companies/companies.constants';
import {
  PoActionDialogComponent,
  PoActionDialogResult,
} from '../po-action-dialog/po-action-dialog.component';
import { PoConfirmDialogComponent } from '../purchase-order-detail/po-confirm-dialog/po-confirm-dialog.component';
import { PoDocumentPreviewDialogComponent } from '../purchase-order-detail/po-document-preview-dialog/po-document-preview-dialog.component';
import { CompanyFieldFactory } from '../company-form/company-form.factory';
import { FH_FIELD } from '../company-form/company-form.constants';
import { isFieldVisible } from '../company-form/company-form.rules';
import {
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhPredicateContext,
} from '../company-form/company-form.types';

// Raw value of a config/option-backed field (type_of_tax_id, city_id, …) before it is resolved to
// its display label. The detail record returns these as a string code or a numeric id.
type ConfigOptionValue = string | number;

// Shape of Angular HttpErrorResponse–like objects thrown by lifecycle action observables.
type ActionError = { status?: number; error?: { message?: string } | string };

// A detail field: a label + the value to display. Empty values fall back to PLACEHOLDER so the
// 2-column grid layout is preserved even when the backend hasn't returned a field yet.
interface DetailField {
  label: string;
  value: string;
}

// A detail section (card): a title + its fields laid out in a 2-column grid.
interface DetailSection {
  title: string;
  fields: DetailField[];
}

// Comment shape consumed by the template (same as the PO view): initials + formatted date derived
// from the raw CompanyComment (author_email / body / created_at).
interface ViewComment {
  id: number;
  author: string;
  initials: string;
  date: string;
  text: string;
}

// A single uploaded file row inside a document card (Figma node 1745:59737): the file name, who
// uploaded it and when (combined into one subtitle), plus the storage key used to open the preview.
interface ViewDocument {
  id: number;
  name: string;
  key: string;
  // "Uploaded by {name} · {date}" — the author half is dropped when the backend doesn't send it.
  subtitle: string;
}

// A document type card: its title (Tax document / Contract) and the files uploaded for it. Only
// built for types that actually have documents, so an empty type renders no card.
interface DocumentCard {
  field: CompanyDocumentField;
  title: string;
  documents: ViewDocument[];
}

// Placeholder shown for fields the current endpoint response does not include (Figma shows ~40
// fields; CompanyRecord has 27). Keeps the layout intact until the backend exposes them.
const PLACEHOLDER = '-';

@Component({
  selector: 'app-company-detail',
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-detail.component.scss'],
})
export class CompanyDetailComponent implements OnInit, OnDestroy {
  loading = true;
  company: CompanyRecord | null = null;
  sections: DetailSection[] = [];
  statusCode: string | null = null;

  // Display values resolved from the FK ids the detail endpoint returns. Contact fields show
  // "Full Name — email"; the group field shows its name. Null until resolved; while null,
  // buildSections() falls back to the raw id so the field is never blank.
  private legalPocDisplay: string | null = null;
  private financialPocDisplay: string | null = null;
  private companyGroupName: string | null = null;

  // Option labels for the fields whose value is an id/code resolved against the per-country backend
  // field config (GET /companies/config/{country}): keyed by field id → (option value → label).
  // Covers type_of_tax_id, city_id, etc. Empty until the config loads; buildSections() falls back
  // to the raw value when a label isn't found.
  private configOptionLabels = new Map<string, Map<string, string>>();

  // Field descriptors merged from companies-form.config.json + the per-country backend field
  // config (same CompanyFieldFactory the create/edit form uses). Drives per-country/per-type
  // field visibility in buildSections() via visible(). Empty until the config loads; a field with
  // no descriptor (e.g. Audit, which has no form counterpart) is treated as always visible.
  private formSections: CompanyFormSection[] = [];
  private fieldDescriptors = new Map<string, CompanyFieldDescriptor>();

  // Comments thread (PRD HU5.D).
  comments: ViewComment[] = [];
  newComment = '';

  // Document preview cards (Figma node 1745:59737): one card per document type (Tax document,
  // Contract) that has at least one uploaded file. Empty until GET /companies/:id/documents loads.
  documentCards: DocumentCard[] = [];

  // Agency Rebates rows (PRD: same sections as the create/edit form, read-only here). Only loaded
  // for type_of_company === agency; empty for every other company type.
  rebates: AgencyRebate[] = [];

  // Expose constants to the template (templates cannot import constants directly).
  readonly STATUS_LABELS = COMPANY_STATUS_LABELS;
  readonly TYPE_LABELS = COMPANY_TYPE_LABELS;
  readonly DOCUMENT_COPY = COMPANY_DOCUMENT_COPY;
  readonly PLACEHOLDER = PLACEHOLDER;
  readonly VIEW_TOOLTIP = COMPANY_VIEW_TOOLTIP;
  readonly COMPANY_TYPE = COMPANY_TYPE;

  // Resolves the status display metadata for the header badge. Normalizes the backend status
  // (which the detail endpoint returns in UPPER_CASE) to the snake_case code used by the labels
  // map; falls back to the raw code when the status isn't mapped so the badge renders without
  // crashing.
  statusMeta(status: string): CompanyStatusMeta {
    const code = normalizeCompanyStatus(status) ?? status;
    return this.STATUS_LABELS[code as CompanyStatus] ?? { label: status, cssClass: code };
  }

  // INI-13125: the backend-computed invoicing classification shown next to the name. Null until
  // the first classification exists (Draft companies); TBD-B1 — access always goes through
  // CLASSIFICATION_API_FIELD so the tentative field name is a one-line swap.
  get invoicingClassification(): InvoicingClassificationValue | null {
    return this.company?.[CLASSIFICATION_API_FIELD] ?? null;
  }

  // cd-status modifier per category — shared with the edit view (company-form.classification.ts).
  readonly classificationCssClass = classificationCssModifier;

  // Skeleton placeholder data — each entry represents one card; fields.length controls the
  // number of shimmer bars shown in the 2-column grid while the real data loads.
  readonly skeletonCards = [
    { fields: Array(6) },  // Company
    { fields: Array(8) },  // Service level
    { fields: Array(9) },  // Compliance & Address
    { fields: Array(22) }, // Invoicing instructions
    { fields: Array(9) },  // Audit
  ];
  readonly skeletonSidebarCards = [
    { fields: Array(2) },  // Approval
  ];

  private readonly destroy$ = new Subject<void>();

  // INI-13125: re-entry guard for submit-for-verification while the classification dry-run is in
  // flight — a second click must not stack a second calculate call or dialog.
  private _isCalculating = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly financesService: MakersFinancesMgrService,
    private readonly lifecycleService: CompanyLifecycleService,
    private readonly permissionsConfig: CompanyPermissionsConfigService,
    private readonly companiesFormConfigService: CompaniesFormConfigService,
    private readonly permissionsService: HasPermissionsService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
  ) {}

  // ── Permission-driven header actions (title-row icons) ───────────────────────
  get canEdit(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.EDIT);
  }

  // Block / Unblock live in the title-row (same placement as Close / Reopen in the PO view).
  get canBlock(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.BLOCK_COMPANY);
  }

  get canUnblock(): boolean {
    return this.permissionsConfig.canPerformAction(this.statusCode, COMPANY_ACTION.UNBLOCK);
  }

  get canComment(): boolean {
    return !this.permissionsConfig.isCommonActionHidden(COMPANY_ACTION.COMMENT, this.statusCode);
  }

  // INI-13919-78: banner data derived from the loaded record's verification_issue (settlement
  // webhook). Null when there is nothing to show — no company, no issue, or no usable error codes.
  // Derived on purpose: a lifecycle refresh replaces `company` and the banner updates for free.
  get verificationBanner(): { message: string; detail: string; background: string; iconColor: string } | null {
    const codes = (this.company?.verification_issue?.errors ?? [])
      .map(error => error.error_code)
      .filter(Boolean);
    if (codes.length === 0) {
      return null;
    }
    return {
      message: COMPANY_VERIFICATION_ISSUE_BANNER.MESSAGE,
      detail: codes.join(', '),
      background: COMPANY_VERIFICATION_ISSUE_BANNER.BG,
      iconColor: COMPANY_VERIFICATION_ISSUE_BANNER.ICON_COLOR,
    };
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || !Number.isFinite(id)) {
      this.router.navigate(['/financial-hub/companies']);
      return;
    }

    this.permissionsConfig.loadConfig().pipe(take(1)).subscribe();

    this.financesService.getCompany(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.company = res.data;
          this.statusCode = normalizeCompanyStatus(res.data.status);
          this.sections = this.buildSections(res.data);
          this.loading = false;
          this._resolveReferenceNames(res.data);
          this._loadComments(id);
          this._loadDocuments(id);
          this._loadRebatesIfAgency(res.data);
        },
        error: () => {
          this.alertsService.openAlerts(
            'Could not load the company. Please try again.',
            AlertsType.error,
          );
          this.router.navigate(['/financial-hub/companies']);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Header: Edit ──────────────────────────────────────────────────────────────
  editCompany(): void {
    if (!this.company) {
      return;
    }
    const country = this.route.snapshot.queryParamMap.get(COMPANY_QUERY_PARAM.COUNTRY);
    const extras = country ? { queryParams: { [COMPANY_QUERY_PARAM.COUNTRY]: country } } : undefined;
    this.router.navigate(['/financial-hub/companies', this.company.id, 'edit'], extras);
  }

  // ── Lifecycle actions (PRD HU5) ─────────────────────────────────────────────────
  // Simple-confirmation actions use PoConfirmDialogComponent; actions that require a
  // comment / reason / justification use PoActionDialogComponent in textarea mode via the
  // COMPANY_DIALOG_CONFIG inline config.

  // INI-13125: before sending a company to verification, preview the classification the submit
  // will assign (a Draft has none yet). The transition modal IS the confirmation on a calculated
  // move — one dialog, not two; an unchanged category, or Continue anyway after a calculation
  // failure, falls back to the generic confirm.
  submitForVerification(): void {
    if (!this.company || this._isCalculating) {
      return;
    }
    this._isCalculating = true;
    this._guardSubmitClassification(this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(step => {
        this._isCalculating = false;
        if (step === CLASSIFICATION_SUBMIT_STEP.RUN) {
          this._run(id => this.lifecycleService.submitForVerification(id), COMPANY_SUCCESS_MESSAGE.SUBMIT, 'submitting for verification');
        } else if (step === CLASSIFICATION_SUBMIT_STEP.CONFIRM) {
          this._confirmSimple(COMPANY_DIALOG_TYPE.SUBMIT, () =>
            this._run(id => this.lifecycleService.submitForVerification(id), COMPANY_SUCCESS_MESSAGE.SUBMIT, 'submitting for verification'),
          );
        }
      });
  }

  verify(): void {
    this._confirmSimple(COMPANY_DIALOG_TYPE.VERIFY, () =>
      this._run(id => this.lifecycleService.verify(id), COMPANY_SUCCESS_MESSAGE.VERIFY, 'verifying'),
    );
  }

  retryComplianceValidation(): void {
    this._confirmSimple(COMPANY_DIALOG_TYPE.RETRY_COMPLIANCE, () =>
      this._run(id => this.lifecycleService.retryCompliance(id), COMPANY_SUCCESS_MESSAGE.RETRY_COMPLIANCE, 'retrying compliance validation'),
    );
  }

  unblock(): void {
    this._confirmSimple(COMPANY_DIALOG_TYPE.UNBLOCK, () =>
      this._run(id => this.lifecycleService.unblock(id), COMPANY_SUCCESS_MESSAGE.UNBLOCK, 'unblocking'),
    );
  }

  returnToKam(): void {
    this._confirmWithText(COMPANY_DIALOG_TYPE.RETURN_TO_KAM, reason =>
      this._run(id => this.lifecycleService.returnToKam(id, reason), COMPANY_SUCCESS_MESSAGE.RETURN_TO_KAM, 'returning to KAM'),
    );
  }

  blockCompany(): void {
    this._confirmWithText(COMPANY_DIALOG_TYPE.BLOCK, reason =>
      this._run(id => this.lifecycleService.blockCompany(id, reason), COMPANY_SUCCESS_MESSAGE.BLOCK, 'blocking'),
    );
  }

  forceVerification(): void {
    this._confirmWithText(COMPANY_DIALOG_TYPE.FORCE_VERIFICATION, reason =>
      this._run(id => this.lifecycleService.forceVerification(id, reason), COMPANY_SUCCESS_MESSAGE.FORCE_VERIFICATION, 'force verifying'),
    );
  }

  forceFinancialValidation(): void {
    this._confirmWithText(COMPANY_DIALOG_TYPE.FORCE_FINANCIAL_VALIDATION, reason =>
      this._run(id => this.lifecycleService.forceFinancialValidation(id, reason), COMPANY_SUCCESS_MESSAGE.FORCE_FINANCIAL_VALIDATION, 'forcing financial validation'),
    );
  }

  // ── Comments ────────────────────────────────────────────────────────────────────
  addComment(): void {
    if (!this.company || !this.newComment.trim()) {
      return;
    }
    this.lifecycleService.addComment(this.company.id, this.newComment.trim())
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.newComment = '';
          this.alertsService.openAlerts(COMPANY_SUCCESS_MESSAGE.COMMENT, AlertsType.success);
          if (this.company) {
            this._loadComments(this.company.id);
          }
        },
        error: (err) => this._handleActionError(err, 'adding comment'),
      });
  }

  getInitialsColor(initials: string): string {
    const colors = ['#E65100', '#1565C0', '#2E7D32', '#6A1B9A', '#AD1457', '#00695C'];
    const idx = (initials.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  }

  // ── Internal helpers ──────────────────────────────────────────────────────────────

  // INI-13125: dry-run behind the detail page's submit-for-verification. A calculated move (or
  // first classification) is confirmed by the transition modal, which resolves RUN; an unchanged
  // category resolves CONFIRM (today's generic-confirm behavior); anything else — a
  // non-calculated item, an empty results array, or an HTTP error — goes to the failure dialog,
  // whose Try again re-runs the whole guard. catchError sits AFTER the item switchMap so the
  // failure dialog's decision flows straight to the retry check (same reasoning as
  // company-form's _guardClassification).
  private _guardSubmitClassification(companyId: number): Observable<ClassificationSubmitStep> {
    return this.financesService.calculateClassification(companyId).pipe(
      map(res => {
        const result = res.data?.results?.[0];
        if (!result) {
          throw new Error('classification-calculate returned no results');
        }
        return result;
      }),
      switchMap(result => {
        if (result.status !== CLASSIFICATION_CALCULATE_STATUS.CALCULATED || !result.calculated) {
          return this._resolveCalculationFailure();
        }
        const current = result.current_invoicing_classification ?? null;
        const next = result.calculated.classification;
        if (current && current === next) {
          return of(CLASSIFICATION_SUBMIT_STEP.CONFIRM);
        }
        return this.dialog
          .open(ClassificationChangeDialogComponent, { width: '576px', data: { current, next } })
          .afterClosed()
          .pipe(map(confirmed =>
            confirmed === true ? CLASSIFICATION_SUBMIT_STEP.RUN : CLASSIFICATION_SUBMIT_STEP.ABORT));
      }),
      catchError(() => this._resolveCalculationFailure()),
      switchMap(step => step === CLASSIFICATION_SUBMIT_STEP.RETRY
        ? this._guardSubmitClassification(companyId)
        : of(step)),
    );
  }

  // Failure dialog for the submit preview: Try again re-runs the guard, Submit anyway falls back
  // to the generic confirm, and a dismissal (X / ESC) aborts the whole action.
  private _resolveCalculationFailure(): Observable<ClassificationSubmitStep> {
    return this.dialog
      .open(ClassificationCalculationErrorDialogComponent, {
        width: '576px',
        data: { continueLabel: CLASSIFICATION_CONTINUE_LABEL.SUBMIT },
      })
      .afterClosed()
      .pipe(map(answer => {
        if (answer === CLASSIFICATION_CALCULATION_ERROR_DIALOG_RESULT.RETRY) {
          return CLASSIFICATION_SUBMIT_STEP.RETRY;
        }
        return answer === CLASSIFICATION_CALCULATION_ERROR_DIALOG_RESULT.CONTINUE
          ? CLASSIFICATION_SUBMIT_STEP.CONFIRM
          : CLASSIFICATION_SUBMIT_STEP.ABORT;
      }));
  }

  // Opens the reusable confirmation modal (no text) and runs `onConfirm` when accepted.
  private _confirmSimple(dialogType: CompanyDialogType, onConfirm: () => void): void {
    const config = COMPANY_DIALOG_CONFIG[dialogType];
    const ref = this.dialog.open(PoConfirmDialogComponent, {
      width: '460px',
      panelClass: 'poc-dialog-panel',
      data: { title: config.title, message: config.message, confirmLabel: config.confirm },
    });
    ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (confirmed) {
        onConfirm();
      }
    });
  }

  // Opens the reusable action modal in textarea mode (required comment/reason/justification) and
  // runs `onConfirm` with the entered text when accepted.
  private _confirmWithText(dialogType: CompanyDialogType, onConfirm: (reason: string) => void): void {
    const config = COMPANY_DIALOG_CONFIG[dialogType];
    const ref = this.dialog.open(PoActionDialogComponent, {
      width: '576px',
      panelClass: 'pocd-dialog-panel',
      data: { config },
    });
    ref.afterClosed().pipe(take(1)).subscribe((result: PoActionDialogResult | undefined) => {
      if (result?.confirmed) {
        onConfirm(result.reason ?? '');
      }
    });
  }

  // Runs a lifecycle call for the current company, shows a success toast, and refreshes the detail.
  private _run(call: (id: number) => Observable<{ message: string }>, successMessage: string, action: string): void {
    if (!this.company) {
      return;
    }
    call(this.company.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alertsService.openAlerts(successMessage, AlertsType.success);
          this._refreshDetails();
        },
        error: (err) => this._handleActionError(err, action),
      });
  }

  private _handleActionError(err: unknown, action: string): void {
    const e = err as ActionError;
    const status = e?.status;
    const serverMessage = e?.error && typeof e.error !== 'string'
      ? e.error.message ?? null
      : (typeof e?.error === 'string' ? e.error : null);
    if (serverMessage) {
      this.alertsService.openAlerts(serverMessage, AlertsType.warning);
    } else if (status === 404) {
      this.alertsService.openAlerts('Company not found.', AlertsType.warning);
    } else {
      this.alertsService.openAlerts(`Error ${action} company. Please try again.`, AlertsType.error);
    }
  }

  private _refreshDetails(): void {
    if (!this.company) {
      return;
    }
    this.financesService.getCompany(this.company.id)
      .pipe(take(1))
      .subscribe(res => {
        this.company = res.data;
        this.statusCode = res.data.status ?? null;
        this.sections = this.buildSections(res.data);
        this._resolveReferenceNames(res.data);
        this._loadComments(res.data.id);
        this._loadDocuments(res.data.id);
        this._loadRebatesIfAgency(res.data);
      });
  }

  private _loadComments(id: number): void {
    this.lifecycleService.getComments(id)
      .pipe(take(1))
      .subscribe({
        next: res => {
          this.comments = (res.data ?? []).map(c => this._toViewComment(c));
        },
        // A failed comments fetch should not break the detail view; leave the thread empty.
        error: () => {
          this.comments = [];
        },
      });
  }

  private _toViewComment(c: CompanyComment): ViewComment {
    const author = c.author_email ?? '';
    return {
      id: c.id,
      author,
      initials: this._getInitials(author),
      date: this._formatDateTime(c.created_at),
      text: c.body,
    };
  }

  // GET /companies/:id/documents (all types, all files) → one card per document type that has
  // files, in COMPANY_DOCUMENT_ORDER. A failed fetch leaves the cards empty rather than breaking
  // the detail view, mirroring _loadComments.
  private _loadDocuments(id: number): void {
    this.financesService.getCompanyDocuments(id)
      .pipe(take(1))
      .subscribe({
        next: res => {
          this.documentCards = this._buildDocumentCards(res.data ?? []);
        },
        error: () => {
          this.documentCards = [];
        },
      });
  }

  // Groups the flat document list by field_name into the ordered, non-empty cards the template
  // renders. Types with no uploaded files are skipped so no empty card is shown. The Contract card
  // is further gated to agency companies, matching companies-form.config.json's section-level
  // visible_when: { type_of_company: ["agency"] } for the "Contract" section.
  private _buildDocumentCards(records: CompanyDocumentRecord[]): DocumentCard[] {
    const isAgency = this.company?.type_of_company === COMPANY_TYPE.AGENCY;
    return COMPANY_DOCUMENT_ORDER
      .filter(field => field !== COMPANY_DOCUMENT_FIELD.CONTRACT || isAgency)
      .map(field => {
        const documents = records
          .filter(doc => doc.field_name === field)
          .map(doc => this._toViewDocument(doc));
        return { field, title: COMPANY_DOCUMENT_LABELS[field], documents };
      }).filter(card => card.documents.length > 0);
  }

  // GET /companies/{id}/agency-rebates (PRD: Agency Rebates is read-only in the detail view).
  // Only fetched for agency companies, mirroring the config's section-level visible_when so no
  // unnecessary call is made for every other company type.
  private _loadRebatesIfAgency(c: CompanyRecord): void {
    if (c.type_of_company !== COMPANY_TYPE.AGENCY) {
      this.rebates = [];
      return;
    }
    this.financesService.listAgencyRebates(c.id)
      .pipe(take(1))
      .subscribe({
        next: res => {
          this.rebates = res.data ?? [];
        },
        error: () => {
          this.rebates = [];
        },
      });
  }

  private _toViewDocument(doc: CompanyDocumentRecord): ViewDocument {
    return {
      id: doc.id,
      name: doc.document_name,
      key: doc.document_key,
      subtitle: this._uploadedSubtitle(doc),
    };
  }

  // "Uploaded by {name} · {date}" (Figma node 1745:59737). `uploaded_by` isn't in the current
  // documents contract, so it's read defensively and the author half is dropped when absent,
  // leaving just the formatted upload date.
  private _uploadedSubtitle(doc: CompanyDocumentRecord): string {
    const author = (doc as { uploaded_by?: string | null }).uploaded_by?.trim();
    const date = this._formatDateTime(doc.uploaded_at);
    const by = author ? `${COMPANY_DOCUMENT_COPY.UPLOADED_BY} ${author}` : '';
    if (by && date) {
      return `${by} · ${date}`;
    }
    return by || date;
  }

  // Opens the shared document preview dialog with the file streamed from GET /files/preview, the
  // same flow the company form's files card uses. The blob URL is handed to the dialog as-is.
  openDocumentPreview(doc: ViewDocument): void {
    this.financesService.previewFile(doc.key, this.storeAux.country)
      .pipe(take(1))
      .subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          this.dialog.open(PoDocumentPreviewDialogComponent, {
            width: '95vw',
            maxWidth: '95vw',
            height: '90vh',
            panelClass: 'podp-panel',
            data: { url, name: doc.name, mimeType: blob.type },
          });
        },
        error: () => {
          this.alertsService.openAlerts(COMPANY_DOCUMENT_COPY.PREVIEW_FAILED, AlertsType.error);
        },
      });
  }

  private _getInitials(author: string): string {
    if (!author) {
      return '';
    }
    const parts = author.split(/[\s@.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return author.slice(0, 2).toUpperCase();
  }

  private _formatDateTime(dateStr: string | null): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  private yesNo(value: boolean | null | undefined): string {
    return value ? 'Yes' : 'No';
  }

  // Formats an ISO timestamp as a short, readable date. Returns the placeholder for empty values.
  private formatDate(iso: string | null | undefined): string {
    if (!iso) {
      return PLACEHOLDER;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return PLACEHOLDER;
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  // Resolves the FK ids / option codes the detail endpoint returns into display values, then
  // rebuilds the sections. Contacts (legal_poc_id / financial_poc_id) show "Full Name — email";
  // company_group_id shows the group name; type_of_tax_id / city_id and other option-backed fields
  // are mapped to their label via the per-country backend field config. Each lookup degrades
  // gracefully: a failed call leaves the value null/empty and buildSections() shows the raw id.
  // Runs in parallel; only hits an endpoint for ids that are actually present.
  private _resolveReferenceNames(c: CompanyRecord): void {
    const legalPoc$ = c.legal_poc_id != null
      ? this.financesService.getContact(c.legal_poc_id).pipe(
          map(res => this._contactDisplay(res.data)),
          catchError(() => of(null)),
        )
      : of(null);

    const financialPoc$ = c.financial_poc_id != null
      ? this.financesService.getContact(c.financial_poc_id).pipe(
          map(res => this._contactDisplay(res.data)),
          catchError(() => of(null)),
        )
      : of(null);

    const companyGroup$ = c.company_group_id != null
      ? this.financesService.getCompanyGroup(c.company_group_id).pipe(
          map(res => res.data?.name ?? null),
          catchError(() => of(null)),
        )
      : of(null);

    // Per-country field config supplies the option labels for id/code-backed fields (type_of_tax_id,
    // city_id, …). is_international is read from the company so the fiscal field set matches.
    const config$ = this.financesService
      .getCompanyFieldConfig(c.country, c.is_international_company)
      .pipe(
        map(res => res.data?.fields ?? []),
        catchError(() => of([])),
      );

    // Static layout config (companies-form.config.json) — same source the create/edit form reads
    // (CompaniesFormConfigService) — merged with `fields` above via CompanyFieldFactory so this
    // view can apply the same per-country/per-type visible_when rules as the form.
    const json$ = this.companiesFormConfigService.getConfig().pipe(
      catchError(() => of(null)),
    );

    forkJoin({ legalPoc: legalPoc$, financialPoc: financialPoc$, companyGroup: companyGroup$, fields: config$, json: json$ })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ legalPoc, financialPoc, companyGroup, fields, json }) => {
        this.legalPocDisplay = legalPoc;
        this.financialPocDisplay = financialPoc;
        this.companyGroupName = companyGroup;
        this.configOptionLabels = this._indexOptionLabels(fields);
        if (json) {
          this.formSections = CompanyFieldFactory.buildSections(json, fields);
          this.fieldDescriptors = new Map(
            this.formSections.flatMap(section => section.fields.map(field => [field.fieldId, field] as const)),
          );
        }
        if (this.company) {
          this.sections = this.buildSections(this.company);
        }
      });
  }

  // Builds the predicate context the rules engine (isFieldVisible) evaluates visible_when against,
  // sourced from the loaded CompanyRecord instead of a live form (there is no form in the detail
  // view). `values` is keyed by controlName, matching what the descriptors expect.
  private _buildPredicateContext(c: CompanyRecord): FhPredicateContext {
    const values: Record<string, unknown> = {};
    const byFieldId = new Map<string, string>();
    this.formSections.forEach(section => section.fields.forEach(field => {
      values[field.controlName] = (c as unknown as Record<string, unknown>)[field.payload];
      byFieldId.set(field.fieldId, field.controlName);
    }));

    return {
      values,
      country: c.country,
      byFieldId,
      isAdmin: this.permissionsService.isAdmin(),
      isFinanceApprover: this.permissionsService.checkPermission(COMPANY_PERMISSIONS.FINANCE_APPROVER),
      hasIntegrationSettlementId: !!c.integration_settlement_id,
    };
  }

  // A field with no companies-form.config.json entry (e.g. every Audit field, which has no form
  // counterpart) fails open — always visible — so this never changes its behavior.
  private visible(fieldId: string, ctx: FhPredicateContext): boolean {
    const descriptor = this.fieldDescriptors.get(fieldId);
    return !descriptor || isFieldVisible(descriptor, ctx);
  }

  // Builds "Full Name — email" from a contact, falling back to whichever part is present.
  private _contactDisplay(contact: ContactCreateResponse | null | undefined): string | null {
    if (!contact) {
      return null;
    }
    const name = contact.full_name?.trim();
    const email = contact.email?.trim();
    if (name && email) {
      return `${name} — ${email}`;
    }
    return name || email || null;
  }

  // Indexes the backend field config into field id → (option value → label) for value→label
  // resolution. Option values are compared as strings since the detail record returns them that way.
  private _indexOptionLabels(fields: { id: string; options?: { value: string; label: string }[] }[]): Map<string, Map<string, string>> {
    const index = new Map<string, Map<string, string>>();
    for (const field of fields) {
      if (!field.options?.length) {
        continue;
      }
      const byValue = new Map<string, string>();
      for (const option of field.options) {
        byValue.set(String(option.value), option.label);
      }
      index.set(field.id, byValue);
    }
    return index;
  }

  // Resolves a field's raw value to its config option label, falling back to the raw value.
  private optionLabel(fieldId: string, value?: ConfigOptionValue): string {
    if (value == null || value === '') {
      return PLACEHOLDER;
    }
    return this.configOptionLabels.get(fieldId)?.get(String(value)) ?? String(value);
  }

  // Combines an author email with a timestamp into a single "email • date" string for the audit
  // "by/at" fields. Falls back to whichever part is present, or the placeholder when both are empty.
  private byAt(email: string | null | undefined, iso: string | null | undefined): string {
    const date = iso ? this.formatDate(iso) : '';
    const hasDate = date && date !== PLACEHOLDER;
    if (email && hasDate) {
      return `${email} • ${date}`;
    }
    if (email) {
      return email;
    }
    return hasDate ? date : PLACEHOLDER;
  }

  // Builds the 5 detail sections from the Figma design (node 1596:68903), gating each field by the
  // same companies-form.config.json visible_when rules the create/edit form applies (country,
  // company type, role, sibling-field predicates) via visible()/isFieldVisible. A fieldId with no
  // config entry (Country, Company ID, all of Audit — none have a form counterpart) is always
  // included. Fields that are visible but empty still render, with PLACEHOLDER as the value.
  private buildSections(c: CompanyRecord): DetailSection[] {
    const ctx = this._buildPredicateContext(c);

    const f = (fieldId: string, label: string, value: string | null | undefined): DetailField | null =>
      this.visible(fieldId, ctx) ? { label, value: value || PLACEHOLDER } : null;

    const section = (title: string, fields: (DetailField | null)[]): DetailSection =>
      ({ title, fields: fields.filter((field): field is DetailField => field !== null) });

    const numeric = (value: number | null | undefined): string =>
      value != null ? String(value) : PLACEHOLDER;

    return [
      section('Company', [
        f('_country', 'Country', c.country),
        f('_company_id', 'Company ID', c.maker_code),
        f('commercial_name', 'Commercial Name', c.name),
        f('type_of_company', 'Company Type', c.type_of_company_raw ?? this.TYPE_LABELS[c.type_of_company as CompanyType]),
        f('company_group', 'Company Group', this.companyGroupName ?? numeric(c.company_group_id)),
        f('international_company', 'International company', this.yesNo(c.is_international_company)),
        f('internationally_billed_country', 'Internationally Billed Country', c.internationally_billed_country),
        f('transaction_currency', 'Transaction Currency', c.transaction_currency),
        f('test_company', 'Test company', this.yesNo(c.is_test)),
        // Always shown (no role / config gating), so it is built directly instead of through f().
        // An absent flag counts as enabled (default ON).
        { label: COMPANY_TRATO_LABEL.ENABLED_FOR_TRATO, value: this.yesNo(c.trato_enabled !== false) },
      ]),
      section('Service level', [
        f('account_manager_email', 'Account manager', c.account_manager_email),
        f('second_account_manager_email', '2nd account manager', c.second_account_manager_email),
        f('coe', 'Center of Excellence (COE)', c.coe),
        f('has_internal_agency', 'International agency', this.yesNo(c.has_internal_agency)),
        f('_tier_of_company', 'Tier of company', PLACEHOLDER),
        f('conciliation_manager_email','Conciliation Manager', c.conciliation_manager_email),
        f('collector_emails', 'Collector',(c.collector_emails ?? []).join(', ')),
        f('squad', 'Squad', c.squad),
      ]),
      section('Compliance & Address', [
        f('tax_id', 'Tax ID', c.tax_id),
        f('social_reason', 'Legal Name', c.social_reason),
        f('type_of_tax_id', 'Type of Tax ID', this.optionLabel('type_of_tax_id', c.type_of_tax_id)),
        f('tax_regime', 'Tax regime', c.tax_regime),
        f('contributor_type', 'Contributor Type', c.contributor_type),
        f('contributor_class', 'Contributor class', c.contributor_class),
        f('payment_method', 'Payment Method', this.optionLabel('payment_method', c.payment_method)),
        f('citizenship', 'Citizenship', this.optionLabel('citizenship', c.citizenship)),
        f('economic_activity', 'Economic Activity', c.economic_activity),
        f('cfdi', 'CFDI', c.cfdi),
        f('legal_poc_id','Legal POC', this.legalPocDisplay ?? numeric(c.legal_poc_id)),
        f('address', 'Address', c.address),
        f('city_id', 'City', this.optionLabel('city_id', c.city_id)),
        f('state_id', 'State', this.optionLabel('state_id', c.state_id)),
        f('province_id', 'Province', this.optionLabel('province_id', c.province_id)),
        f('commune_id', 'Commune', this.optionLabel('commune_id', c.commune_id)),
        f('postal_code', 'Postal code', c.postal_code),
      ]),
      section('Invoicing instructions', [
        f('payment_terms', 'Payment terms', c.payment_terms),
        f('billing_criteria', 'Billing criteria', c.billing_criteria),
        f('billing_frequency', 'Billing frequency', c.billing_frequency),
        f('financial_cutoff_day','Financial cut-off day', numeric(c.financial_cutoff_day)),
        f('financial_poc_id','Financial POC', this.financialPocDisplay ?? numeric(c.financial_poc_id)),
        f('invoicing_email', 'Invoicing email', c.invoicing_email),
        f('inquires_email', 'Inquires email', c.inquires_email),
        f('phone', 'Phone number', c.phone),
        f('is_invoice_upload_needed', 'Is Invoice Upload Needed', this.yesNo(c.is_invoice_upload_needed)),
        f('requires_invoice_upload', 'Requires invoice upload portal', this.yesNo(c.requires_invoice_upload)),
        f('invoice_portal_url', 'Invoice portal URL', c.invoice_portal_url),
        f(
          FH_FIELD.PROOF_OF_EXECUTION_REQUIREMENTS,
          'Proof of Execution Requirements',
          this.optionLabel(FH_FIELD.PROOF_OF_EXECUTION_REQUIREMENTS, c.proof_of_execution_requirements),
        ),
        f('requires_good_receipt', 'Requires good receipt', this.yesNo(c.requires_good_receipt)),
        f('is_oc_required', 'OC Required', this.yesNo(c.is_oc_required)),
        f('should_the_oc_be_fully_consumed_before_invoiced', 'OC fully consumed before invoiced', this.yesNo(c.oc_fully_consumed_before_invoiced)),
        f('should_the_oc_be_signed_by_both_parties', 'Signed by both parties', this.yesNo(c.oc_signed_by_both_parties)),
        f('agency_fee', 'Agency Fee (%)', numeric(c.agency_fee)),
        f('invoice_comments', 'Invoice comments', c.invoice_comments),
        f('is_migo_number_needed', 'MIGO Number Needed?', this.yesNo(c.is_migo_number_needed)),
        f('is_proof_of_execution_required', 'Proof of Execution Required?', this.yesNo(c.is_proof_of_execution_required)),
        f('is_gr_needed', 'Is GR Needed', this.yesNo(c.is_gr_needed)),
        f('is_payment_complement_needed', 'Payment Complement Needed?', this.yesNo(c.is_payment_complement_needed)),
        f('is_hes_number_needed', 'HES Number Needed?', this.yesNo(c.is_hes_number_needed)),
        f('is_conciliation_letter_needed', 'Conciliation Letter Needed?', this.yesNo(c.is_conciliation_letter_needed)),
        f('is_entrance_number_needed', 'Entrance Number Needed?', this.yesNo(c.is_entrance_number_needed)),
        f('is_another_number_needed', 'Another Number Needed?', this.yesNo(c.is_another_number_needed)),
        f('has_special_invoice_acceptance_process', 'Special Invoice Acceptance Process?', this.yesNo(c.has_special_invoice_acceptance_process)),
        f('special_invoice_acceptance_detail', 'Special Invoice Acceptance Detail', c.special_invoice_acceptance_detail),
      ]),
      // Audit has no companies-form.config.json counterpart — always rendered, same as today.
      section('Audit', [
        f('_settlement_participant_id', 'Settlement Participant ID', c.integration_settlement_id),
        f('_settlement_payment_reference_id', 'Settlement Payment Reference ID', c.settlement_payment_reference_id),
        f('_created_by_at', 'Created by/at', this.byAt(c.catalog_created_by_email, c.catalog_created_at ?? c.created_at)),
        f('_last_modified_by_at', 'Last modified by/at', this.byAt(c.updated_by, c.updated_at)),
        f('_last_approved_by_at', 'Verified by/at', this.byAt(c.verified_by_email, c.verified_at)),
      ]),
    ];
  }
}
